import React, { useState, useEffect } from 'react';
import {
  SpaceBetween,
  Box,
  Header,
  Alert,
  Badge,
  Container,
  Button,
  Spinner,
  ColumnLayout,
  StatusIndicator,
  TextContent,
  Icon
} from '@cloudscape-design/components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import apiService from '../services/api';

/**
 * GuardrailsShowcase — Interactive demonstration of Bedrock Guardrails.
 * 
 * Shows 4 clickable question cards. Safe questions return KB-backed answers
 * with a green "Passed" indicator. Guardrail-triggering questions show the
 * intervention with a red "Intervened" indicator.
 */
function GuardrailsShowcase() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState({});  // keyed by question_id

  // Load questions on mount
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const response = await apiService.getGuardrailsQuestions();
        setQuestions(response.questions || []);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load guardrails questions:', err);
        setError('Guardrails showcase not available');
        setLoading(false);
      }
    };
    loadQuestions();
  }, []);

  const handleAskQuestion = async (questionId) => {
    setActiveQuestion(questionId);
    setProcessing(true);

    try {
      const response = await apiService.askGuardrailsQuestion(questionId);
      setResults(prev => ({ ...prev, [questionId]: response }));
    } catch (err) {
      console.error('Guardrails question failed:', err);
      setResults(prev => ({
        ...prev,
        [questionId]: {
          guardrail_status: 'error',
          response: `Error: ${err.message}`,
          execution_time: 0
        }
      }));
    } finally {
      setProcessing(false);
      setActiveQuestion(null);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'factual': return '📊';
      case 'investment_advice': return '💰';
      case 'financial_speculation': return '🔮';
      default: return '❓';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'factual': return '#0073bb';
      case 'investment_advice': return '#e67e22';
      case 'financial_speculation': return '#e67e22';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return (
      <Container>
        <SpaceBetween size="m" alignItems="center">
          <Spinner size="large" />
          <Box textAlign="center">Loading guardrails showcase...</Box>
        </SpaceBetween>
      </Container>
    );
  }

  if (error) {
    return (
      <Alert type="warning" header="Guardrails Showcase">
        {error}. This feature requires a Bedrock Guardrail to be configured.
      </Alert>
    );
  }

  return (
    <SpaceBetween size="l">
      <Container>
        <SpaceBetween size="m">
          <Header
            variant="h2"
            description="Click a question to see how Bedrock Guardrails protect your AI research pipeline. Safe questions get full KB-backed answers. Guardrail-triggering questions show the safety controls in action."
          >
            🛡️ Bedrock Guardrails — Interactive Demo
          </Header>

          <Alert type="info" dismissible>
            <TextContent>
              <p>
                These questions use the <strong>same Knowledge Base and Strands agent framework</strong> as 
                the main research pipeline, but with <strong>Amazon Bedrock Guardrails</strong> configured. 
                Try clicking each question to see which ones pass and which ones trigger the guardrail.
              </p>
            </TextContent>
          </Alert>
        </SpaceBetween>
      </Container>

      {/* Question Cards */}
      <ColumnLayout columns={2} variant="text-grid">
        {questions.map((q) => {
          const result = results[q.id];
          const isActive = activeQuestion === q.id;
          const hasResult = !!result;
          const isIntervened = result?.guardrail_status === 'intervened';
          const isPassed = result?.guardrail_status === 'passed';

          return (
            <div
              key={q.id}
              data-guardrail-state={isIntervened ? 'intervened' : isPassed ? 'passed' : isActive ? 'active' : 'idle'}
              style={{
                border: `2px solid ${
                  isIntervened ? '#d62728' :
                  isPassed ? '#16a085' :
                  isActive ? '#0073bb' :
                  '#e0e0e0'
                }`,
                borderRadius: '12px',
                padding: '20px',
                background: isIntervened
                  ? 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'
                  : isPassed
                  ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                  : '#ffffff',
                cursor: processing ? 'wait' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: (processing && !isActive) ? 0.6 : 1
              }}
              onClick={() => !processing && handleAskQuestion(q.id)}
            >
              <SpaceBetween size="m">
                {/* Question Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '1.4em' }}>{getCategoryIcon(q.category)}</span>
                    <Badge color={q.category === 'factual' ? 'blue' : 'grey'}>
                      {q.category.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  {hasResult && (
                    isIntervened ? (
                      <Badge color="red">🛡️ Guardrail Intervened</Badge>
                    ) : (
                      <Badge color="green">✅ Guardrail Passed</Badge>
                    )
                  )}
                </div>

                {/* Question Text */}
                <Box fontSize="heading-s" fontWeight="bold" color="text-body-primary">
                  {q.question}
                </Box>

                {/* Loading State */}
                {isActive && processing && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Spinner size="normal" />
                    <Box color="text-body-secondary">Querying with guardrails enabled...</Box>
                  </div>
                )}

                {/* Result */}
                {hasResult && !isActive && (
                  <div style={{
                    borderTop: `1px solid ${isIntervened ? '#fca5a5' : '#86efac'}`,
                    paddingTop: '12px',
                    marginTop: '4px'
                  }}>
                    {isIntervened ? (
                      <SpaceBetween size="s">
                        <div
                          data-guardrail-result="intervened"
                          style={{
                          background: '#dc262620',
                          border: '1px solid #fca5a5',
                          borderRadius: '8px',
                          padding: '12px'
                        }}>
                          <SpaceBetween size="xs">
                            <Box fontWeight="bold" color="text-status-error">
                              🛡️ Guardrail Intervention
                            </Box>
                            <Box fontSize="body-s">
                              {result.response}
                            </Box>
                          </SpaceBetween>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <Badge color="red">
                            Policy: {result.guardrail_details?.policy_name || 'Denied Topic'}
                          </Badge>
                          <Badge color="grey">
                            {result.execution_time?.toFixed(1)}s
                          </Badge>
                        </div>
                      </SpaceBetween>
                    ) : (
                      <SpaceBetween size="s">
                        <div
                          data-guardrail-result="passed"
                          style={{
                          background: '#16a08510',
                          border: '1px solid #86efac',
                          borderRadius: '8px',
                          padding: '12px',
                          maxHeight: '200px',
                          overflow: 'auto'
                        }}>
                          <Box fontSize="body-s" style={{ lineHeight: '1.6' }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {result.response}
                            </ReactMarkdown>
                          </Box>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <Badge color="green">All policies passed</Badge>
                          <Badge color="grey">
                            {result.execution_time?.toFixed(1)}s
                          </Badge>
                        </div>
                      </SpaceBetween>
                    )}
                  </div>
                )}

                {/* Click prompt */}
                {!hasResult && !isActive && (
                  <Box fontSize="body-s" color="text-body-secondary">
                    Click to ask this question →
                  </Box>
                )}
              </SpaceBetween>
            </div>
          );
        })}
      </ColumnLayout>
    </SpaceBetween>
  );
}

export default GuardrailsShowcase;
